import { MIN_ENROLLED_LIVES } from "@/utils/validations/enrollmentValidation";
import { deriveCoverageMonths, type CoverageMonth } from "./deriveCoverageMonths";

/**
 * Coverage months an accommodation run reads: from the policy start through
 * `throughMonth` (YYYY-MM-DD, first of month) for a monthly run, or the whole
 * term when `throughMonth` is omitted (year-end reconciliation).
 */
export function requiredCoverageMonths(
  effectiveStart: string | undefined,
  effectiveEnd: string | undefined,
  throughMonth?: string
): CoverageMonth[] {
  const months = deriveCoverageMonths(effectiveStart, effectiveEnd);
  return throughMonth ? months.filter((m) => m.coverageMonth <= throughMonth) : months;
}

/** Months with no enrolled lives, or fewer than MIN_ENROLLED_LIVES (a stored 0). */
export function missingCoverageMonths(
  months: readonly CoverageMonth[],
  livesByMonth: ReadonlyMap<string, number>
): CoverageMonth[] {
  return months.filter((m) => (livesByMonth.get(m.coverageMonth) ?? 0) < MIN_ENROLLED_LIVES);
}

/**
 * "Copy prior month": fills every missing month from the nearest earlier month
 * with enrolled lives, cascading (Aug and Sep both take Jul when both are empty).
 * Missing months before the first populated month have no prior and stay empty.
 * Returns only the filled months.
 */
export function fillFromPriorMonth(
  months: readonly CoverageMonth[],
  livesByMonth: ReadonlyMap<string, number>
): Map<string, number> {
  const filled = new Map<string, number>();
  let prior: number | undefined;
  for (const m of months) {
    const lives = livesByMonth.get(m.coverageMonth);
    if (lives !== undefined && lives >= MIN_ENROLLED_LIVES) {
      prior = lives;
    } else if (prior !== undefined) {
      filled.set(m.coverageMonth, prior);
    }
  }
  return filled;
}
